import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Avatar,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  Alert,
  Snackbar
} from '@mui/material';
import ReportIcon from '@mui/icons-material/Report';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';
import AdminSocketService, { ADMIN_SOCKET_EVENTS } from '../../services/AdminSocketService';

const Reports = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  const reportController = useMemo(() => new CONTROLLERS.ReportController(api), [api]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReports, setTotalReports] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterType, setFilterType] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [contentViewOpen, setContentViewOpen] = useState(false);
  const [contentViewReport, setContentViewReport] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'reports',
        params,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 60 * 1000 });
      if (Array.isArray(cached?.reports)) {
        setReports(cached.reports);
        setTotalReports(Number.isFinite(cached.total) ? cached.total : 0);
      }

      const response = await reportController.getReports(params);
      setReports(response.reports || []);
      setTotalReports(response.total || 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { reports: response.reports || [], total: response.total || 0 },
        { scope: 'reports' },
      );
    } catch (error) {
      console.error('Error fetching reports:', error);
      showSnackbar('Failed to fetch reports', 'error');
    } finally {
      setLoading(false);
    }
  }, [auth?.user?.id, page, rowsPerPage, filterStatus, filterType]);

  const fetchStats = useCallback(async () => {
    try {
      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'report_stats',
        params: { v: 1 },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 60 * 1000 });
      if (cached?.stats) {
        setStats(cached.stats);
      }

      const statsData = await reportController.getReportStats();
      setStats(statsData);

      await SERVICES.cacheAdminList(cacheKey, { stats: statsData }, { scope: 'report_stats' });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [auth?.user?.id, reportController]);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [fetchReports, fetchStats]);

  useEffect(() => {
    const socketService = AdminSocketService.getInstance();

    const handleReportCreated = () => {
      showSnackbar('New report received', 'info');
      fetchReports();
      fetchStats();
    };

    const off = socketService.on(ADMIN_SOCKET_EVENTS.REPORT_CREATED, handleReportCreated);
    socketService.connect();

    return () => {
      try {
        off?.();
      } catch (_) {}
    };
  }, [fetchReports, fetchStats]);

  const handleViewContent = async (report) => {
    try {
      const fullReport = await reportController.getReportById(report.id);
      setContentViewReport(fullReport);
      setContentViewOpen(true);
    } catch (error) {
      console.error('Error fetching report content:', error);
      setContentViewReport(report);
      setContentViewOpen(true);
    }
  };

  const handleViewDetails = async (report) => {
    try {
      const fullReport = await reportController.getReportById(report.id);
      setSelectedReport(fullReport);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching report details:', error);
      showSnackbar('Failed to fetch report details', 'error');
    }
  };

  const handleAction = (report, action) => {
    setSelectedReport(report);
    setActionType(action);
    setAdminNotes('');
    setActionDialogOpen(true);
  };

  const executeAction = async () => {
    try {
      if (actionType === 'resolve') {
        await reportController.resolveReport(selectedReport.id, adminNotes);
        showSnackbar('Report resolved successfully', 'success');
      } else if (actionType === 'dismiss') {
        await reportController.dismissReport(selectedReport.id, adminNotes);
        showSnackbar('Report dismissed successfully', 'success');
      } else if (actionType === 'review') {
        await reportController.setUnderReview(selectedReport.id, adminNotes);
        showSnackbar('Report set to under review', 'success');
      }
      setActionDialogOpen(false);
      fetchReports();
      fetchStats();
    } catch (error) {
      console.error('Error executing action:', error);
      showSnackbar('Failed to update report', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const mockReports = [
    {
      id: 1,
      reporter: {
        name: 'John Doe',
        email: 'john.doe@example.com',
        avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
      },
      reportedUser: {
        name: 'Jane Smith',
        email: 'jane.smith@example.com',
        avatar: 'https://randomuser.me/api/portraits/women/1.jpg'
      },
      reason: 'Inappropriate Content',
      description: 'User posted content that violates community guidelines',
      status: 'pending',
      priority: 'high',
      date: '2024-01-15',
      time: '14:30'
    },
    {
      id: 2,
      reporter: {
        name: 'Mike Johnson',
        email: 'mike.johnson@example.com',
        avatar: 'https://randomuser.me/api/portraits/men/2.jpg'
      },
      reportedUser: {
        name: 'Sarah Wilson',
        email: 'sarah.wilson@example.com',
        avatar: 'https://randomuser.me/api/portraits/women/2.jpg'
      },
      reason: 'Spam',
      description: 'User is posting repetitive promotional content',
      status: 'resolved',
      priority: 'medium',
      date: '2024-01-14',
      time: '09:15'
    },
    {
      id: 3,
      reporter: {
        name: 'Emily Brown',
        email: 'emily.brown@example.com',
        avatar: 'https://randomuser.me/api/portraits/women/3.jpg'
      },
      reportedUser: {
        name: 'David Lee',
        email: 'david.lee@example.com',
        avatar: 'https://randomuser.me/api/portraits/men/3.jpg'
      },
      reason: 'Harassment',
      description: 'User is sending threatening messages',
      status: 'investigating',
      priority: 'high',
      date: '2024-01-13',
      time: '16:45'
    },
    {
      id: 4,
      reporter: {
        name: 'Alex Chen',
        email: 'alex.chen@example.com',
        avatar: 'https://randomuser.me/api/portraits/men/4.jpg'
      },
      reportedUser: {
        name: 'Lisa Garcia',
        email: 'lisa.garcia@example.com',
        avatar: 'https://randomuser.me/api/portraits/women/4.jpg'
      },
      reason: 'Fake Account',
      description: 'User appears to be using a fake identity',
      status: 'pending',
      priority: 'low',
      date: '2024-01-12',
      time: '11:20'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'under_review':
        return 'info';
      case 'resolved':
        return 'success';
      case 'dismissed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <WarningIcon />;
      case 'under_review':
        return <VisibilityIcon />;
      case 'resolved':
        return <CheckCircleIcon />;
      case 'dismissed':
        return <BlockIcon />;
      default:
        return <ReportIcon />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'post':
        return <PostAddIcon />;
      case 'user':
        return <PersonIcon />;
      case 'company':
        return <BusinessIcon />;
      default:
        return <ReportIcon />;
    }
  };

  const formatReason = (reason) => {
    return reason.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getReportedItemDisplay = (report) => {
    if (report.type === 'post' && report.reportedPost) {
      return {
        name: report.reportedPost.caption?.substring(0, 50) + '...' || 'Post',
        author: report.reportedPost.author?.username || 'Unknown'
      };
    } else if (report.type === 'user' && report.reportedUser) {
      return {
        name: report.reportedUser.username || 'User',
        author: report.reportedUser.email || ''
      };
    } else if (report.type === 'company' && report.reportedCompany) {
      return {
        name: report.reportedCompany.name || 'Company',
        author: report.reportedCompany.email || ''
      };
    }
    return { name: 'Unknown', author: '' };
  };

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>Reports</Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Manage user reports and content violations
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="dismissed">Dismissed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="post">Post</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="company">Company</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Summary Cards */}
        {stats && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{stats.total}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Reports</Typography>
                    </Box>
                    <Badge badgeContent={stats.pending} color="warning">
                      <ReportIcon color="primary" />
                    </Badge>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{stats.pending}</Typography>
                      <Typography variant="body2" color="text.secondary">Pending</Typography>
                    </Box>
                    <WarningIcon color="warning" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{stats.resolved}</Typography>
                      <Typography variant="body2" color="text.secondary">Resolved</Typography>
                    </Box>
                    <CheckCircleIcon color="success" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{stats.underReview}</Typography>
                      <Typography variant="body2" color="text.secondary">Under Review</Typography>
                    </Box>
                    <VisibilityIcon color="info" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Reports Table */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Recent Reports
              </Typography>
              <Button variant="outlined" color="primary">
                Export Reports
              </Button>
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Reporter</TableCell>
                        <TableCell>Reported Item</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" py={4}>
                              No reports found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        reports.map((report) => {
                          const reportedItem = getReportedItemDisplay(report);
                          return (
                            <TableRow key={report.id}>
                              <TableCell>
                                <Chip
                                  label={report.type.toUpperCase()}
                                  size="small"
                                  icon={getTypeIcon(report.type)}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 32, height: 32 }}>
                                    {report.reporter?.username?.[0]?.toUpperCase() || 'U'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {report.reporter?.username || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {report.reporter?.email || ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell
                                onClick={() => handleViewContent(report)}
                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              >
                                <Typography variant="body2" fontWeight={600} color="primary">
                                  {reportedItem.name}
                                </Typography>
                                {reportedItem.author && (
                                  <Typography variant="caption" color="text.secondary">
                                    by {reportedItem.author}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatReason(report.reason)}
                                </Typography>
                                {report.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200 }}>
                                    {report.description.substring(0, 50)}...
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={report.status.replace('_', ' ').toUpperCase()}
                                  color={getStatusColor(report.status)}
                                  size="small"
                                  icon={getStatusIcon(report.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(report.createdAt).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(report.createdAt).toLocaleTimeString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="View Details">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleViewDetails(report)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                {report.status === 'pending' && (
                                  <>
                                    <Tooltip title="Set Under Review">
                                      <IconButton 
                                        size="small" 
                                        color="info"
                                        onClick={() => handleAction(report, 'review')}
                                      >
                                        <VisibilityIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Resolve">
                                      <IconButton 
                                        size="small" 
                                        color="success"
                                        onClick={() => handleAction(report, 'resolve')}
                                      >
                                        <CheckCircleIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Dismiss">
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => handleAction(report, 'dismiss')}
                                      >
                                        <BlockIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalReports}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Reported Item Content Dialog */}
        <Dialog
          open={contentViewOpen}
          onClose={() => setContentViewOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reported Item Content</DialogTitle>
          <DialogContent>
            {contentViewReport && (
              <Box>
                {/* Post Content */}
                {contentViewReport.type === 'post' && contentViewReport.reportedPost && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Avatar sx={{ width: 36, height: 36 }}>
                        {contentViewReport.reportedPost.author?.username?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          @{contentViewReport.reportedPost.author?.username || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(contentViewReport.reportedPost.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                    {contentViewReport.reportedPost.caption && (
                      <Typography variant="body1" sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {contentViewReport.reportedPost.caption}
                      </Typography>
                    )}
                    {contentViewReport.reportedPost.imageUrls?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {contentViewReport.reportedPost.imageUrls.map((url, i) => (
                          <Box
                            key={i}
                            component="img"
                            src={url}
                            alt={`Post image ${i + 1}`}
                            sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
                {/* User Content */}
                {contentViewReport.type === 'user' && contentViewReport.reportedUser && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Avatar sx={{ width: 48, height: 48 }}>
                      {contentViewReport.reportedUser.username?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        @{contentViewReport.reportedUser.username}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contentViewReport.reportedUser.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {/* Company Content */}
                {contentViewReport.type === 'company' && contentViewReport.reportedCompany && (
                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {contentViewReport.reportedCompany.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {contentViewReport.reportedCompany.email}
                    </Typography>
                    {contentViewReport.reportedCompany.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {contentViewReport.reportedCompany.description}
                      </Typography>
                    )}
                  </Box>
                )}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Reason:</strong> {formatReason(contentViewReport.reason)}
                  </Typography>
                  {contentViewReport.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      <strong>Description:</strong> {contentViewReport.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContentViewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Report Details Dialog */}
        <Dialog 
          open={detailsOpen} 
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Report Details</DialogTitle>
          <DialogContent>
            {selectedReport && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                    <Chip 
                      label={selectedReport.type.toUpperCase()} 
                      icon={getTypeIcon(selectedReport.type)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={selectedReport.status.replace('_', ' ').toUpperCase()} 
                      color={getStatusColor(selectedReport.status)}
                      icon={getStatusIcon(selectedReport.status)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Reason</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {formatReason(selectedReport.reason)}
                    </Typography>
                  </Grid>
                  {selectedReport.description && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedReport.description}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Reporter</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {selectedReport.reporter?.username || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedReport.reporter?.email || ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Reported At</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {new Date(selectedReport.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                  {selectedReport.reviewedBy && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Reviewed By</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {selectedReport.reviewedBy.username}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Reviewed At</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {new Date(selectedReport.reviewedAt).toLocaleString()}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {selectedReport.adminNotes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Admin Notes</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedReport.adminNotes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Action Dialog */}
        <Dialog 
          open={actionDialogOpen} 
          onClose={() => setActionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {actionType === 'resolve' && 'Resolve Report'}
            {actionType === 'dismiss' && 'Dismiss Report'}
            {actionType === 'review' && 'Set Under Review'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add notes about your decision (optional):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Enter your notes here..."
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={executeAction} 
              variant="contained"
              color={actionType === 'dismiss' ? 'error' : 'primary'}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default Reports; 